import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { map, tap } from 'rxjs/operators';

import { environment } from 'src/environments/environment';
import { UserAuthService } from '../UserAuth.service';
import { CureSheetFailure, CureSheetSummary, EffectiveCureSheets } from './cure-sheet.model';
import { CureSheetEntitlement } from './cure-sheet-entitlement';
import { cureSheetEntitlement$, publishCureSheetEntitlement } from './cure-sheet-entitlement-store';

/**
 * The cure-sheet BEARER path — the only tier a browser may call.
 *
 * NOT THE DEVICE PLANE. That tier authenticates a CMS unit by HMAC using a key
 * derived from its DPS provisioning key, and resolves which customer's sheets to
 * serve from a SERIAL. A browser has neither. Faking one would mean shipping the
 * enrollment-group key in the Angular bundle — a public artifact — and that key
 * lets its holder derive any device's key and enrol devices. It is a provisioning
 * credential and must never reach the client.
 *
 * Auth here is the Entra bearer the app already sends, attached by the MSAL
 * interceptor, plus tenantId/objectId[/customerId] identifying the user's scope.
 */
@Injectable({ providedIn: 'root' })
export class CureSheetsService {
  constructor(private http: HttpClient, private userAuth: UserAuthService) {}

  private identityParams(): HttpParams | null {
    const tenantId = this.userAuth.getCurrentCustomerTenantId();
    const objectId = this.userAuth.getObjectId();
    const customerId = this.userAuth.getActiveCustomerId();

    // Missing identity is a CALLER bug (the server answers 400), not something to
    // show a user. Null tells the caller to stay quiet rather than fire a request
    // that cannot succeed.
    if (!tenantId || !objectId) return null;

    let params = new HttpParams().set('tenantId', tenantId).set('objectId', objectId);
    if (customerId) params = params.set('customerId', customerId);
    return params;
  }

  /**
   * The audience list: active library ∩ what this customer has EXPOSED, approved
   * versions only, one version per sheet.
   *
   * The response also carries the COMPANY-LEVEL entitlement flag. Joel ruled
   * (2026-09-06) that a company has cure-sheet access iff at least one of its
   * devices is subscribed — resolved server-side, because per-device entitlement
   * cannot be evaluated by a console that has no device.
   */
  loadEffective(): Observable<{ sheets: CureSheetSummary[]; entitlement: CureSheetEntitlement }> {
    const params = this.identityParams();
    if (!params) return of({ sheets: [], entitlement: { source: 'unknown', entitled: false } });

    return this.http
      .get<EffectiveCureSheets & { companyHasCureSheetAccess?: boolean }>(
        `${environment.apiUrl}v1/cure-sheets/effective`,
        { params }
      )
      .pipe(
        map((response) => ({
          sheets: Array.isArray(response?.sheets) ? response.sheets : [],
          // A BODYLESS RESPONSE REUSES THE PREVIOUS DECISION — see readEntitlement.
          entitlement: response == null ? cureSheetEntitlement$.value : readEntitlement(response),
        })),
        tap((result) => publishCureSheetEntitlement(result.entitlement))
      );
  }

  /**
   * The PDF, as a BLOB through HttpClient.
   *
   * THE INTERCEPTOR ONLY SEES HttpClient. An <iframe src>, <embed src>, <a href>
   * or window.open pointed at this route sends NO Authorization header and gets a
   * 401 — while the markup reviews as perfectly correct. That is also why the
   * old ten-year SAS URL was DELETED rather than repointed: a URL that works in an
   * href is exactly a URL carrying its own credential, and that credential
   * outlived approval withdrawal. Access is re-derived on every request here.
   */
  fetchPdf(sheetId: string, version: number): Observable<Blob> {
    const params = this.identityParams();
    if (!params) return throwError(() => new Error('cure-sheet PDF requested without an identity'));

    return this.http.get(
      `${environment.apiUrl}v1/cure-sheets/${encodeURIComponent(sheetId)}/versions/${version}/content`,
      { params, responseType: 'blob' }
    );
  }
}

/**
 * Read the company-level gate off the list response.
 *
 * ABSENT FAILS CLOSED. A server that has not yet shipped the flag is
 * indistinguishable from one answering "no access", and this is paid value: the
 * recoverable mistake is a customer asking where the feature went, not handing it
 * to someone who has not bought it. Reported as source 'unknown' so the screen
 * can say something honest rather than claiming a subscription verdict it never
 * received.
 *
 * A MISSING BODY IS NOT A MISSING FLAG, and the caller must not confuse them.
 * `/effective` supports ETag/If-None-Match, and A 304 HAS NO BODY — so a reader
 * that ran this over an empty response would read "no access" and lock out an
 * entitled customer the moment a revalidation succeeded. 304 means "your previous
 * answer still stands": reuse the previous DECISION, never re-derive it from
 * nothing. Safe to do, because the flag is inside the payload the ETag hashes, so
 * an entitlement change always changes the ETag and cannot hide behind a 304.
 *
 * Today nothing in this app sets If-None-Match, so revalidation is
 * browser-managed and a 304 arrives as a cached 200 WITH a body — verified, not
 * assumed. The guard is here so that adding an ETag layer later cannot silently
 * turn revalidation into a lockout.
 */
export function readEntitlement(response: { companyHasCureSheetAccess?: boolean } | null | undefined): CureSheetEntitlement {
  const flag = response?.companyHasCureSheetAccess;
  if (typeof flag !== 'boolean') return { source: 'unknown', entitled: false };
  return { source: 'server', entitled: flag };
}

/**
 * Map a failure to the state the UI should render.
 *
 * NEVER SHOW THE SERVER'S DENIAL TEXT. A 404 here carries an internal denial
 * marker and a message written for the DEVICE plane — the browser route and the
 * device route share one helper, so its prose refers to a device the console user
 * does not have. The status code and the marker are the contract; the message
 * text is not.
 *
 * The marker and its wording are deliberately NOT quoted anywhere in this
 * codebase, including in comments: minified comments can survive into the bundle,
 * and keeping the literal out entirely makes "that string cannot reach a browser"
 * checkable with a single grep over dist/ rather than a promise.
 *
 * Denials are also deliberately UNIFORM: withdrawn, unexposed and unapproved all
 * return the same 404 so the library cannot be enumerated by probing. The UI
 * therefore cannot say which, and must not guess one.
 */
export function classifyCureSheetError(error: unknown): CureSheetFailure {
  const status = error instanceof HttpErrorResponse ? error.status : 0;
  if (status === 401) return 'unauthenticated';
  if (status === 403) return 'notProvisioned';
  if (status === 404) return 'unavailable';
  if (status >= 500) return 'server';
  return 'server';
}
